import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, History, RefreshCw, RotateCcw, Send, Truck, UserRound } from 'lucide-react';
import {
  CALL_STATUS_LABEL,
  CALL_STATUS_TONE,
  NO_SURVEY_LABEL,
  PERMISSIONS,
  SURVEY_OUTCOME_LABEL,
  SURVEY_OUTCOME_TONE,
  type TrackerFdfcDetail,
  type TrackerFdfcOrder,
} from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { formatDateTime } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';
import { TONE_CLASS } from '@/lib/tone';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AddressSection } from './AddressSection';
import { DispatchBadge } from './DispatchBadge';
import { DispatchAnotherDialog } from './DispatchAnotherDialog';
import { NSDC_STATUS_LABEL } from './dispatch-labels';
import { DetailSection, Field } from './DetailSection';
import { FollowUpSection } from './FollowUpSection';

/**
 * One FDFC Tracker entry on its own page: a summary header, the team's
 * follow-up on the left, contact / address / NSDC dispatch on the right, and
 * the raw webhook history folded away at the bottom. Stacks on small screens.
 */
export function TrackerFdfcEntryPage() {
  const { id = '' } = useParams<{ id: string }>();
  // The list passes its query string (page + filters) so "back" lands on the same view.
  const location = useLocation();
  const backTo = `/tracker-fdfc${(location.state as { search?: string } | null)?.search ?? ''}`;
  const perms = usePermission();
  const canDispatch = perms.has(PERMISSIONS.TRACKER_FDFC_DISPATCH);
  const canUpdate = perms.has(PERMISSIONS.TRACKER_FDFC_UPDATE);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tracker-fdfc', 'detail', id],
    queryFn: () => trackerFdfcService.get(id),
    enabled: !!id,
  });

  const back = (
    <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-3.5 w-3.5" /> FDFC Tracker
    </Link>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {back}
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-3">
        {back}
        <Card className="p-6 text-sm text-muted-foreground">Entry not found. It may have been removed.</Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {back}
      <EntryHeader entry={data} />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.1fr_1fr]">
        <FollowUpSection entry={data} canUpdate={canUpdate} />

        <div className="space-y-4">
          <DetailSection icon={UserRound} title="Contact">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Mobile">{data.mobile_number}</Field>
              <Field label="Alternate phone">{data.alt_phone}</Field>
              <Field label="Course level">{data.course_level}</Field>
              <Field label="Times received">{data.receive_count}</Field>
              <Field label="First received">{formatDateTime(data.first_received_at)}</Field>
              <Field label="Last received">{formatDateTime(data.last_received_at)}</Field>
            </dl>
          </DetailSection>

          <AddressSection entry={data} canUpdate={canUpdate} />

          <DispatchSection entry={data} canDispatch={canDispatch} />
        </div>
      </div>

      <WebhookHistory entry={data} />
    </div>
  );
}

/** Name, the line people scan for, and the three statuses at a glance. */
function EntryHeader({ entry }: { entry: TrackerFdfcDetail }) {
  const survey = entry.survey_outcome;
  return (
    <Card className="p-4 sm:p-5">
      <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{entry.full_name ?? entry.mobile_number}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        <span className="font-mono">{entry.mobile_number}</span>
        {entry.course_level && ` · ${entry.course_level}`}
        {` · received ${entry.receive_count}×`}
        {` (${formatDateTime(entry.first_received_at)}`}
        {entry.receive_count > 1 && ` → ${formatDateTime(entry.last_received_at)}`}
        {')'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusPill label="Survey" className={survey ? TONE_CLASS[SURVEY_OUTCOME_TONE[survey]] : TONE_CLASS.neutral}>
          {survey ? SURVEY_OUTCOME_LABEL[survey] : NO_SURVEY_LABEL}
        </StatusPill>
        <StatusPill label="Call" className={TONE_CLASS[CALL_STATUS_TONE[entry.call_status]]}>
          {CALL_STATUS_LABEL[entry.call_status]}
        </StatusPill>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          NSDC <DispatchBadge entry={entry} inline />
        </span>
      </div>
    </Card>
  );
}

function StatusPill({ label, className, children }: { label: string; className: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold', className)}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
        {children}
      </span>
    </span>
  );
}

/**
 * Every NSDC kit order for this person, newest first, with "Dispatch another
 * order" for a replacement (damaged, lost, cancelled) unless the case is settled.
 */
function DispatchSection({ entry, canDispatch }: { entry: TrackerFdfcDetail; canDispatch: boolean }) {
  const queryClient = useQueryClient();
  const [anotherOpen, setAnotherOpen] = useState(false);
  const afterChange = () => void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
  const refresh = useMutation({
    mutationFn: () => trackerFdfcService.refreshNsdc(entry.id),
    onSuccess: () => {
      toast.success('Status updated from NSDC');
      afterChange();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      afterChange();
    },
  });
  const reset = useMutation({
    mutationFn: () => trackerFdfcService.resetDispatch(entry.id),
    onSuccess: () => {
      toast.success(entry.nsdc_order_code ? 'The earlier order stands — you can dispatch another' : 'Can be sent again');
      afterChange();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const hasOrders = entry.orders.length > 0;
  const canSendAnother = canDispatch && hasOrders && entry.dispatch_status === 'dispatched' && !entry.dispatch_settled;

  return (
    <DetailSection
      icon={Truck}
      title={hasOrders && entry.orders.length > 1 ? `NSDC dispatch · ${entry.orders.length} orders` : 'NSDC dispatch'}
      action={
        <>
          {hasOrders && (
            <Button size="sm" variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} /> Refresh status
            </Button>
          )}
          {canDispatch && (entry.dispatch_status === 'unknown' || entry.dispatch_status === 'failed') && (
            <Button size="sm" variant="outline" onClick={() => reset.mutate()} disabled={reset.isPending}>
              <RotateCcw className="h-4 w-4" /> Allow resend
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {entry.dispatch_error && (
          <p className="rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {entry.dispatch_error}
          </p>
        )}
        {entry.dispatch_status === 'dispatching' && (
          <p className="text-xs text-muted-foreground">An order is being sent to NSDC right now…</p>
        )}

        {entry.orders.map((o, i) => (
          <OrderBlock key={o.id} order={o} latest={i === 0 && entry.orders.length > 1} />
        ))}

        {!hasOrders && entry.dispatch_status === 'not_dispatched' && !entry.dispatch_error && (
          <p className="text-xs text-muted-foreground">Not sent to NSDC yet. Tick the row in the list to dispatch.</p>
        )}

        {hasOrders && entry.dispatch_settled && (
          <p className="text-xs text-muted-foreground">
            Nothing to send — delivered, or the claim was refused. A survey answer with &ldquo;Need to dispatch&rdquo;
            brings it back.
          </p>
        )}
        {canSendAnother && (
          <Button variant="outline" className="w-full" onClick={() => setAnotherOpen(true)}>
            <Send className="h-4 w-4" /> Dispatch another order
          </Button>
        )}

        {entry.dispatch_attempts.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-muted-foreground">
              Send attempts ({entry.dispatch_attempts.length})
            </summary>
            <ul className="mt-1 space-y-1 text-xs">
              {entry.dispatch_attempts.map((a) => (
                <li key={a.id}>
                  {formatDateTime(a.created_at)} · {a.kit_code} ·{' '}
                  {a.succeeded ? (
                    <span className="text-emerald-600 dark:text-emerald-400">{a.order_code}</span>
                  ) : (
                    <span className="text-rose-600 dark:text-rose-400">{a.error}</span>
                  )}
                  {a.created_by_name && ` · by ${a.created_by_name}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {canSendAnother && (
        <DispatchAnotherDialog entry={entry} open={anotherOpen} onClose={() => setAnotherOpen(false)} />
      )}
    </DetailSection>
  );
}

/** One NSDC order: status, kit, when, courier tracking. */
function OrderBlock({ order, latest }: { order: TrackerFdfcOrder; latest: boolean }) {
  const s = order.nsdc_status;
  const variant = s === 'delivered' ? 'success' : s === 'returned' || s === 'cancelled' ? 'destructive' : 'default';
  const t = order.nsdc_tracking;
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={variant}>{s ? NSDC_STATUS_LABEL[s] : 'Sent'}</Badge>
        <span className="font-mono text-xs">{order.order_code}</span>
        {latest && <Badge variant="secondary">Latest</Badge>}
        {order.additional && <Badge variant="outline">Extra order</Badge>}
      </div>
      {order.kit_title && (
        <p className="mt-1.5 text-sm">
          {order.kit_title} <span className="font-mono text-xs text-muted-foreground">({order.kit_code})</span>
        </p>
      )}
      <p className="mt-0.5 text-xs text-muted-foreground">
        Sent {formatDateTime(order.dispatched_at)}
        {order.created_by_name && ` by ${order.created_by_name}`}
        {order.nsdc_synced_at && ` · checked ${formatDateTime(order.nsdc_synced_at)}`}
      </p>
      {order.sync_note && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{order.sync_note}</p>}
      {s === 'pending' && (
        <p className="mt-1 text-xs text-muted-foreground">
          At NSDC, waiting on its Orientation Approvals tab for release to shipping.
        </p>
      )}
      {t && (
        <div className="mt-2 space-y-1 rounded-lg bg-muted/40 p-2.5 text-xs">
          <p className="font-medium">
            {t.courierName ?? t.partnerName ?? t.partner}
            {t.awb && (
              <>
                {' '}
                · AWB <span className="font-mono">{t.awb}</span>
              </>
            )}
            {t.attemptNo > 1 && ` · attempt ${t.attemptNo}`}
          </p>
          {t.trackingUrl && (
            <a
              href={t.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Track parcel <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {t.history.slice(0, 5).map((h, i) => (
            <p key={i} className="text-muted-foreground">
              {formatDateTime(h.occurredAt)} · {h.status}
              {h.location && ` · ${h.location}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** Every webhook call / Excel row that touched this entry, folded away by default. */
function WebhookHistory({ entry }: { entry: TrackerFdfcDetail }) {
  return (
    <Card className="p-0">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-5">
          <History className="h-4 w-4 text-primary" /> Webhook history ({entry.events.length})
          <span className="ml-auto text-[11px] font-normal normal-case group-open:hidden">Show</span>
          <span className="ml-auto hidden text-[11px] font-normal normal-case group-open:inline">Hide</span>
        </summary>
        <ul className="space-y-2 border-t border-border/60 p-4 sm:p-5">
          {entry.events.map((ev) => (
            <li key={ev.id} className="rounded-lg border border-border/60 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs">{formatDateTime(ev.received_at)}</span>
                <Badge
                  variant={ev.status === 'processed' ? 'success' : ev.status === 'failed' ? 'destructive' : 'secondary'}
                >
                  {ev.status}
                </Badge>
              </div>
              {ev.error_message && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{ev.error_message}</p>}
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-muted-foreground">Raw payload</summary>
                <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted/50 p-2 text-[11px] leading-snug">
                  {JSON.stringify(ev.payload, null, 2)}
                </pre>
              </details>
            </li>
          ))}
          {entry.events.length === 0 && <li className="text-xs text-muted-foreground">Nothing received yet.</li>}
        </ul>
      </details>
    </Card>
  );
}
